/* eslint-disable no-useless-concat */
import { ACCESS_TOKEN } from "../constants";
import { AuthService } from "../../services/auth.service";
import { requestTokenRefresh } from "../../services/api.service";

// Get auth API base URL - use environment variable in production, empty string for relative URLs in dev
const AUTH_API_BASE = process.env.REACT_APP_AUTH_API_URL || '';

// Helper function to get token with automatic legacy format migration
const getAccessToken = (): string | null => {
  const token = localStorage.getItem(ACCESS_TOKEN);

  // Handle legacy JSON format: {"access_token":"...","refresh_token":"..."}
  if (token && token.startsWith('{')) {
    try {
      const tokenObj = JSON.parse(token);
      if (tokenObj.access_token) {
        // Migrate to new format
        localStorage.setItem(ACCESS_TOKEN, tokenObj.access_token);
        if (tokenObj.refresh_token) {
          localStorage.setItem('refreshToken', tokenObj.refresh_token);
        }
        return tokenObj.access_token;
      }
    } catch (e) {
      console.error('Failed to parse legacy token format', e);
    }
  }

  return token;
};

const request = (options, _isRetry = false) => {
  const headers = new Headers({
    "Content-Type": "application/json"
  });

  const accessToken = getAccessToken();

  if (accessToken) {
    headers.append("Authorization", "Bearer " + accessToken);
  }

  const defaults = { headers: headers };
  options = Object.assign({}, defaults, options);

  // Add timeout handling (30 seconds)
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, 30000);

  const fetchOptions = {
    ...options,
    signal: controller.signal
  };

  return fetch(fetchOptions.url, fetchOptions)
    .then(response => {
      clearTimeout(timeoutId);

      // Handle 401 — use the shared Axios token refresh mechanism (single refresh, no race condition)
      if (response.status === 401 && !_isRetry) {
        return new Promise((resolve, reject) => {
          requestTokenRefresh(
            (newToken) => {
              // Retry with the new token
              const retryOptions = { ...options };
              retryOptions.headers = new Headers(options.headers);
              retryOptions.headers.set("Authorization", "Bearer " + newToken);
              resolve(request(retryOptions, true));
            },
            reject
          );
        });
      }

      // Check content-type before attempting to parse JSON
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        return response.json().then(json => {
          if (!response.ok) {
            return Promise.reject(json);
          }
          return json;
        });
      } else {
        // Handle non-JSON response (e.g., HTML error pages)
        return response.text().then(text => {
          console.error("API returned non-JSON response:", response.status, text.substring(0, 200));
          return Promise.reject({
            success: false,
            message: `Server error: ${response.status} - API endpoint returned non-JSON response`,
            type: 'INVALID_RESPONSE'
          });
        });
      }
    })
    .catch(error => {
      clearTimeout(timeoutId);

      // Don't treat abort as error if it was intentional cancellation
      if (error.name === 'AbortError') {
        return Promise.reject({
          type: 'TIMEOUT',
          message: 'Request timed out. Please try again.',
          code: 'TIMEOUT'
        });
      }
      // Handle network errors
      if (!error.response && (error.message === 'Network Error' || error.message === 'Failed to fetch' || navigator.onLine === false)) {
        return Promise.reject({
          type: 'NETWORK_ERROR',
          message: 'Connection failed. Please check your internet connection and try again.',
          code: 'NETWORK_ERROR'
        });
      }
      return Promise.reject(error);
    });
};

export function login(loginRequest) {
  return request({
    url: `${AUTH_API_BASE}/apis/auth/signin`,
    method: "POST",
    body: JSON.stringify(loginRequest)
  });
}

export function signout(signoutRequest) {
  // Clear user cache on logout
  clearUserCache();

  return request({
    url: `${AUTH_API_BASE}/apis/auth/signout`,
    method: "POST",
    body: JSON.stringify(signoutRequest)
  });
}

export function signup(signupRequest) {
  return request({
    url: `${AUTH_API_BASE}/apis/auth/signup`,
    method: "POST",
    body: JSON.stringify(signupRequest)
  });
}

export function requestOtpCode(payload) {
  return request({
    url: `${AUTH_API_BASE}/apis/user/requestOtpCode`,
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function otpForgot(signupRequest) {
  return request({
    url: `${AUTH_API_BASE}/apis/auth/otp/forgot`,
    method: "POST",
    body: JSON.stringify(signupRequest)
  });
}

export function questionForgot(signupRequest) {
  return request({
    url: `${AUTH_API_BASE}/apis/auth/question/forgot`,
    method: "POST",
    body: JSON.stringify(signupRequest)
  });
}

export function validateResetToken(token) {
  return request({
    url: `${AUTH_API_BASE}/apis/auth/validate-reset-token`,
    method: "POST",
    body: JSON.stringify({ token })
  });
}

export function active(token) {
  return request({
    url: `${AUTH_API_BASE}/apis/activate/${token}`,
    method: "GET"
  });
}

export function reset(signupRequest) {
  return request({
    url: `${AUTH_API_BASE}/apis/auth/reset`,
    method: "POST",
    body: JSON.stringify(signupRequest)
  });
}

export function resetWithQuestion(signupRequest) {
  return request({
    url: `${AUTH_API_BASE}/apis/auth/resetWithQuestion`,
    method: "POST",
    body: JSON.stringify(signupRequest)
  });
}

export function checkUsernameAvailability(requestBody) {
  return request({
    url: `${AUTH_API_BASE}/apis/user/checkUsernameAvailability`,
    method: "POST",
    body: JSON.stringify(requestBody)
  });
}

export function getUserQuestions(requestBody) {
  return request({
    url: `${AUTH_API_BASE}/apis/auth/user/questions`,
    method: "POST",
    body: JSON.stringify(requestBody)
  });
}

export function getUserFailedCount(requestBody) {
  return request({
    url: `${AUTH_API_BASE}/apis/auth/user/failed/count`,
    method: "POST",
    body: JSON.stringify(requestBody)
  });
}

export function checkEmailAvailability(requestBody) {
  return request({
    url: `${AUTH_API_BASE}/apis/user/checkEmailAvailability`,
    method: "POST",
    body: JSON.stringify(requestBody)
  });
}

// Cache configuration for getCurrentUser
const USER_CACHE_KEY = 'cached_current_user';
const USER_CACHE_TIMESTAMP_KEY = 'cached_current_user_timestamp';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export function getCurrentUser() {
  const accessToken = getAccessToken();
  if (!accessToken) {
    return Promise.reject("No access token set.");
  }

  // Check cache first
  const cachedUser = localStorage.getItem(USER_CACHE_KEY);
  const cachedTimestamp = localStorage.getItem(USER_CACHE_TIMESTAMP_KEY);

  if (cachedUser && cachedTimestamp) {
    const age = Date.now() - parseInt(cachedTimestamp, 10);
    if (age < CACHE_DURATION_MS) {
      console.log('⚡ Using cached user data (age: ' + Math.round(age / 1000) + 's)');
      return Promise.resolve(JSON.parse(cachedUser));
    } else {
      console.log('🔄 Cache expired (age: ' + Math.round(age / 1000) + 's), fetching fresh data');
    }
  }

  // Fetch fresh data
  return request({
    url: `${AUTH_API_BASE}/apis/user/me`,
    method: "GET"
  }).then(user => {
    // Cache the result
    try {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
      localStorage.setItem(USER_CACHE_TIMESTAMP_KEY, Date.now().toString());
      console.log('💾 Cached fresh user data');
    } catch (e) {
      console.warn('Failed to cache user data:', e);
    }
    return user;
  });
}

// Helper to clear user cache (call on logout)
export function clearUserCache() {
  localStorage.removeItem(USER_CACHE_KEY);
  localStorage.removeItem(USER_CACHE_TIMESTAMP_KEY);
}

export function getUserProfile(username) {
  return request({
    url: `${AUTH_API_BASE}/apis/users/${username}`,
    method: "GET"
  });
}

export function getUserSourceByActiveToken(token) {
  return request({
    url: `${AUTH_API_BASE}/apis/user/getUserSourceByActiveToken?token=${token}`,
    method: "GET"
  });
}
